/**
 * Auth 相关接口
 * https://wiki.tigerbrokers.net/pages/viewpage.action?pageId=11832379
 */
import Vue from 'vue'
import parseJsonWithBigInt from 'utils/parseJsonWithBigInt'
import {
  encryptLoginPassword,
  encryptTradePassword
} from '@tiger/api-signature'

// 对请求参数字段进行加密处理
function encryptLogin(data) {
  return {
    ...data,
    login_password: encryptLoginPassword(data.login_password)
  }
}
function encryptTrade(data) {
  return {
    ...data,
    trade_password: encryptTradePassword(data.trade_password)
  }
}

/**
 * 注册账号（手机号|邮箱）
 * @param {object} data
 * @returns {PromiseLike<HttpResponse>}
 */
export function signupAccount(data) {
  return Vue.http
    .post('~oauth/api/v1/auth/signup', encryptLogin(data), {
      emulateJSON: true
    })
    .then(res => {
      if (res.data.is_succ) return res.data.data
      throw res.data.data
    })
}
/**
 * 用户快速登录
 * @param {object} data
 * @returns {PromiseLike<HttpResponse>}
 */
export function fastSigninAccount(data) {
  return Vue.http
    .post('~oauth/api/v1/auth/fast/signin', encryptLogin(data), {
      credentials: true
    })
    .then(res => {
      if (res.data.is_succ) return res.data.data
      throw res.data.data
    })
}
/**
 * 用户登录
 * @param {object} data
 * @returns {PromiseLike<HttpResponse>}
 */
export function signinAccount(data) {
  return Vue.http
    .post('~oauth/api/v1/auth/signin', encryptLogin(data), {
      credentials: true
    })
    .then(res => {
      if (res.data.is_succ) return res.data.data
      throw res.data.data
    })
}
/**
 * 刷新token
 * @param {object} data
 * @returns {PromiseLike<HttpResponse>}
 */
export function refreshAuthToken(data) {
  return Vue.http.post('~oauth/api/v1/auth/refresh', data)
}
/**
 * 登出账户
 * @returns {PromiseLike<HttpResponse>}
 */
export function signoutAccount() {
  return Vue.http.post('~oauth/api/v1/auth/signout')
}

/**
 * 获取验证码
 * @returns {PromiseLike<HttpResponse>}
 */
export function getVerificationCaptcha() {
  return Vue.http.get('~oauth/api/v1/verification/captcha', {
    credentials: true
  })
}
/**
 * 获取极验验证码
 * @returns {PromiseLike<HttpResponse>}
 */
export function getGeetestVerification() {
  return Vue.http.get('~oauth/api/v1/verification/captcha/register', {
    credentials: true
  })
}
/**
 * 发送验证邮件
 * @param {object} data
 * @param {string} data.process - 业务场景（登录，注册等等）
 * @param {string} data.email - 邮箱
 * @param {string} data.captcha_code - 图片验证码
 * @param {string} data.category - 业务类型（美国版固定为individual_us）
 * @returns {PromiseLike<HttpResponse>}
 */
export function sendVerificationEmail(data) {
  data.category = 'individual'
  return Vue.http.post('~oauth/api/v1/verification/send/email', data, {
    credentials: true
  })
}
/**
 * 发送手机验证码
 * @param {object} data
 * @param {string} data.process - 业务场景（登录，注册等等）
 * @param {string} data.tel_code - 手机区号
 * @param {string} data.phone - 手机
 * @param {string} data.captcha_code - 图片验证码
 * @param {string} data.category - 业务类型（美国版固定为individual_us）
 * @returns {PromiseLike<HttpResponse>}
 */
export function sendVerificationMessage(data) {
  data.category = 'individual'
  return Vue.http.post('~oauth/api/v1/verification/send/phone', data, {
    credentials: true
  })
}

/**
 * 切换设备（权限抢占）
 * @returns {PromiseLike<HttpResponse>}
 */
export function reqDeviceSwitch() {
  return Vue.http.post('~oauth/api/v2/device/switch').then(res => {
    if (res.is_succ) return res.data
    throw res.data
  })
}
/**
 * 获取用户信息
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchUserInfo() {
  return Vue.http.get('~oauth/api/v1/user/info').then(res => {
    if (res.data.is_succ) return res.data.data
    throw res.data
  })
}

/**
 * 获取账号状态信息
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchAccountStatusInfo() {
  return Vue.http.get('~tesseractHk/api/v4/account/status').then(response => {
    if (response) {
      const res = parseJsonWithBigInt(response.bodyText)
      if (res.is_succ) return res.data
      throw res
    } else {
      throw response
    }
  })
}
/**
 * 获取账户权限信息
 * @param {string} params.account_type
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchTradePermission(params) {
  return Vue.http
    .get('~tesseract/api/v2/account/permission', { params })
    .then(res => {
      if (res.data.is_succ) return res.data.data
      throw res.data
    })
}

/**
 * 获取行情权限信息
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchQuotePermission() {
  return Vue.http.get('~hqPerm/api/v1/permission').then(res => {
    if (res.data.is_succ) return res.data.data
    throw res.data
  })
}

/**
 * 获取交易授权
 * @param {object} data
 * @returns {PromiseLike<HttpResponse>}
 */
export function reqTradeAuthorize(data) {
  return Vue.http.post(
    '~oauth/api/v1/auth/trade/authorize',
    encryptTrade(data),
    {
      emulateJSON: true
    }
  )
}
/**
 * 答题签署期权协议
 * @param {string} data.real_name 真名
 * @returns {PromiseLike<HttpResponse>}
 */
export function signOptionAgreement(data) {
  return Vue.http.put('~oauth/api/v1/auth/option', data, {
    emulateJSON: true
  })
}
/**
 * 创建交易
 * @param {object} data
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchCreateTrade(data) {
  data.trade_password = encryptTradePassword(data.trade_password)
  return Vue.http.post('~oauth/api/v1/security/set/trade_password', data, {
    emulateJSON: true
  })
}

/**
 * 重置交易密码
 * @param {object} data
 * @returns {PromiseLike<HttpResponse>}
 */
export function resetTradePwd(data) {
  data.trade_password = encryptTradePassword(data.trade_password)
  return Vue.http.post('~oauth/api/v1/security/reset/trade_password', data, {
    emulateJSON: true
  })
}

/**
 * 切换交易账户
 * @param {object} data
 * @param {string} data.account_type 账号类型[ib/nd/bs/virtual]
 * @param {string} data.account_id 账号（有值时传）
 * @param {string} data.customer_id 客户号（有值时传）
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchSwitchTradeAccount(data) {
  return Vue.http
    .post('~oauth/api/v2/account/switch', data, {
      emulateJSON: true
    })
    .then(res => {
      if (res.data.is_succ) return res.data
      throw res.data
    })
}
/**
 * 获取两步验证的状态
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchTwoStepVerifyStatus() {
  return Vue.http.get('~oauth/api/v1/otp/status').then(res => {
    if (res.data.is_succ) return res.data.data
    throw res.data.data
  })
}
/**
 * 设置两步验证
 * @param {object} data
 * @returns {PromiseLike<HttpResponse>}
 */
export function setTwoStepVerify(data) {
  return Vue.http.post('~oauth/api/v1/otp/setting', data).then(res => {
    if (res.data.is_succ) return res.data.data
    throw res.data.data
  })
}

/**
 * 获取初始化设备初始化信息
 * @returns {PromiseLike<HttpResponse>}
 */
export function fetchDeviceInit() {
  return Vue.http.get('~oauth/api/v1/device/init')
}

/**
 * 验证密码
 * @param {string} process 业务场景
 * @param {string} login_password 是否登录密码
 * @returns {PromiseLike<HttpResponse>}
 */
export function verifyPassword(data) {
  return Vue.http.post(
    '~oauth/api/v1/verification/verify/password',
    encryptLogin(data)
  )
}

/**
 * 验证手机验证码
 * @param {string} process 业务场景
 * @param {string} verify_code 验证码
 * @returns {PromiseLike<HttpResponse>}
 */
export function verifyPhoneCode(data) {
  return Vue.http.post('~oauth/api/v1/verification/verify/phone', data)
}

/**
 * 验证邮箱验证码
 * @param {string} process 业务场景
 * @param {string} verify_code 验证码
 * @returns {PromiseLike<HttpResponse>}
 */
export function verifyEmailCode(data) {
  return Vue.http.post('~oauth/api/v1/verification/verify/email', data)
}

/**
 * 验证老虎令牌
 * @param {string} process 业务场景
 * @param {string} otp_code 是否登录密码
 * @returns {PromiseLike<HttpResponse>}
 */
export function verifyTradeUpToken(data) {
  return Vue.http.post('~oauth/api/v1/verification/verify/otp', data)
}

/**
 * 获取验证方式
 * @param {string} [username] 用户名
 * @returns {PromiseLike<HttpResponse>}
 */
export function getVerifyTypes(data) {
  return Vue.http.post('~oauth/api/v1/verification/verify/options', data)
}
